/**
 * TABLETTES MURALES — le modèle des écrans (02/11/2026).
 *
 * Tout vient de kiosk-api, relayé par /api/v1/tablettes (aucune copie dans
 * Gestion), selon le contrat kiosk-api 1.1.0 (packages/contracts/src/tablettes/v1).
 * Chaque champ est lu avec prudence : un champ absent ou nul s'affiche « — »,
 * jamais un zéro inventé. Fonctions pures, testées sans navigateur.
 */

export const CHEMIN_TABLETTES = '/technique/tablettes'
export const CHEMIN_ENROLER = '/technique/tablettes/enroler'
export const cheminTablette = (id: string): string => `${CHEMIN_TABLETTES}/${encodeURIComponent(id)}`
/** Contrat kiosk-api : IdTablette. */
export const ID_TABLETTE_RX = /^[a-z0-9-]{1,40}$/

/** Les emplacements : ROOM 1 / ROOM 2 / ROOM 3, jamais les anciens noms. */
export const SALLES = ['ROOM 1', 'ROOM 2', 'ROOM 3', 'Accueil', 'Bureau', 'Technique', 'Autre'] as const

export interface EtatTablette {
  batterie?: number | null
  en_charge?: boolean | null
  branchee?: boolean | null
  temperature?: number | null
  rssi?: number | null
  ip?: string | null
  app?: string | null
  android?: string | null
  webview?: string | null
  maj_systeme?: string | boolean | null
  dernier_toucher?: string | null
  uptime_min?: number | null
  ecran?: 'ON' | 'OFF' | null
  ecran_allume?: boolean | null
  ecran_methode?: 'A' | 'B' | null
  luminosite?: number | 'auto' | null
  volume?: number | null
  url?: string | null
  dashboard?: RefDashboard | null
  kiosque_ouvert?: boolean | null
  bloque?: boolean | null
  bloque_texte?: string | null
  bloque_style?: StyleBlocage | null
  lecture_seule?: { actif: boolean; dashboard: RefDashboard | null } | null
  maintenance_fin?: string | null
  recu_le?: string | null
}

export interface RefDashboard { dashboard: string; vue: string }
export type StyleBlocage = 'hors_service' | 'menage' | 'cadenas'
export const STYLES_BLOCAGE: { value: StyleBlocage; label: string; texteParDefaut: string }[] = [
  { value: 'hors_service', label: 'Hors service', texteParDefaut: 'Hors service' },
  { value: 'menage', label: 'Ménage', texteParDefaut: 'Ménage en cours' },
  { value: 'cadenas', label: 'Cadenas', texteParDefaut: '' },
]

export interface Tablette {
  id: string
  nom?: string | null
  salle?: string | null
  mode?: string | null
  ha_compte?: string | null
  compte_mqtt?: 'gere' | 'externe' | null
  en_ligne?: boolean | null
  vu_le?: string | null
  capture_le?: string | null
  etat?: EtatTablette | null
}

export interface DocParc { genere_le: string; mqtt_connecte: boolean | null; tablettes: Tablette[] }
export interface DocFiche { lu_le: string; tablette: Tablette }
export interface Geste { le: string; heure: string; quoi: string; qui: string | null; issue: 'ok' | 'inconnue' }

const estObjet = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

export function estTablette(v: unknown): v is Tablette {
  return estObjet(v) && typeof v['id'] === 'string' && ID_TABLETTE_RX.test(v['id'])
}
export function estDocParc(v: unknown): v is DocParc {
  return estObjet(v) && Array.isArray(v['tablettes'])
}
export function estDocFiche(v: unknown): v is DocFiche {
  return estObjet(v) && estTablette(v['tablette'])
}

/* ─────────────────────────── l'état lisible ─────────────────────────── */

export type Ton = 'ok' | 'warn' | 'err' | 'off' | 'maint'

export const SEUIL_BATTERIE_BASSE = 20
export const SEUIL_BATTERIE_MOYENNE = 50
export const SEUIL_CHAUDE = 42

export function libelleBlocage(style: StyleBlocage | null | undefined): string {
  return style === 'menage' ? 'Ménage en cours' : style === 'cadenas' ? 'Verrouillée' : 'Hors service'
}

const nombre = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/** Vrai si la maintenance court encore. */
export function enMaintenance(t: Tablette, maintenant = Date.now()): boolean {
  const fin = t.etat?.maintenance_fin ? Date.parse(t.etat.maintenance_fin) : NaN
  return Number.isFinite(fin) && fin > maintenant
}

/**
 * LA pastille d'une tablette, dans l'ordre de ce qui compte : hors ligne,
 * hors service, maintenance, chaude, sur batterie, en ligne.
 */
export function etatTablette(t: Tablette, maintenant = Date.now()): { ton: Ton; texte: string } {
  const e = t.etat ?? {}
  if (t.en_ligne === false) {
    const vu = t.vu_le ? Date.parse(t.vu_le) : NaN
    return { ton: 'off', texte: Number.isFinite(vu) ? `Hors ligne depuis ${HEURE.format(new Date(vu))}` : 'Hors ligne' }
  }
  if (e.bloque) return { ton: 'err', texte: libelleBlocage(e.bloque_style) }
  if (enMaintenance(t, maintenant)) {
    const reste = Math.ceil((Date.parse(e.maintenance_fin!) - maintenant) / 60_000)
    return { ton: 'maint', texte: `Maintenance · ${reste} min` }
  }
  const temp = nombre(e.temperature)
  if (temp !== null && temp >= SEUIL_CHAUDE) return { ton: 'warn', texte: 'Chaude' }
  if (e.branchee === false && e.en_charge !== true) return { ton: 'warn', texte: 'Sur batterie' }
  if (t.en_ligne === true) return { ton: 'ok', texte: 'En ligne' }
  return { ton: 'off', texte: 'État inconnu' }
}

/** À surveiller : tout sauf « En ligne ». */
export const aSurveiller = (t: Tablette, maintenant = Date.now()): boolean => etatTablette(t, maintenant).ton !== 'ok'

export function batterie(t: Tablette): { pct: number | null; texte: string; ton: Ton } {
  const pct = nombre(t.etat?.batterie)
  if (pct === null) return { pct: null, texte: '—', ton: 'off' }
  const charge = t.etat?.en_charge === true || t.etat?.branchee === true
  const ton: Ton = pct < SEUIL_BATTERIE_BASSE ? 'err' : pct < SEUIL_BATTERIE_MOYENNE && !charge ? 'warn' : 'ok'
  return { pct: Math.max(0, Math.min(100, Math.round(pct))), texte: `${Math.round(pct)} %${charge ? ' ⚡' : ''}`, ton }
}

export function temperature(t: Tablette): string {
  const v = nombre(t.etat?.temperature)
  return v === null ? '—' : `${Math.round(v)} °C`
}

export function ecran(t: Tablette): string {
  const e = t.etat ?? {}
  if (e.bloque) return 'Bloquée'
  if (e.lecture_seule?.actif) return 'Lecture seule'
  if (e.ecran === 'OFF') return `Éteint${e.ecran_methode ? ` (${e.ecran_methode})` : ''}`
  if (e.ecran === 'ON') return e.luminosite === 'auto' ? 'Écran auto' : nombre(e.luminosite) !== null ? `Écran ${e.luminosite} %` : 'Écran allumé'
  return '—'
}

const HEURE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })
const HEURE_S = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', second: '2-digit' })

export function heure(iso: string | null | undefined, secondes = false): string {
  const d = iso ? Date.parse(iso) : NaN
  return Number.isFinite(d) ? (secondes ? HEURE_S : HEURE).format(new Date(d)) : '—'
}

/** « il y a 4 s », « il y a 12 min », « il y a 3 h ». */
export function ilYa(iso: string | null | undefined, maintenant = Date.now()): string {
  const d = iso ? Date.parse(iso) : NaN
  if (!Number.isFinite(d)) return '—'
  const s = Math.max(0, Math.round((maintenant - d) / 1000))
  if (s < 60) return `il y a ${s} s`
  const m = Math.round(s / 60)
  if (m < 60) return `il y a ${m} min`
  const h = Math.round(m / 60)
  if (h < 48) return `il y a ${h} h`
  return `il y a ${Math.round(h / 24)} j`
}

export function duree(min: number | null | undefined): string {
  if (typeof min !== 'number' || !Number.isFinite(min) || min < 0) return '—'
  if (min < 60) return `${Math.round(min)} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} h ${String(Math.round(min % 60)).padStart(2, '0')}`
  return `${Math.floor(h / 24)} j ${h % 24} h`
}

export function wifi(rssi: number | null | undefined): string {
  return typeof rssi === 'number' && Number.isFinite(rssi) ? `${Math.round(rssi)} dBm` : '—'
}

/** La version de capture : sert de clé de cache à l'image (une nouvelle capture = une nouvelle URL). */
export function cleCapture(t: Tablette): string {
  return t.capture_le ?? ''
}
export function urlCapture(t: Tablette, taille: 'vignette' | 'pleine'): string {
  const v = cleCapture(t)
  return `/api/v1/tablettes/${encodeURIComponent(t.id)}/capture?taille=${taille}${v ? `&v=${encodeURIComponent(v.slice(0, 40))}` : ''}`
}

/** « Bar ROOM 3 » → « bar-room-3 » (contrat : minuscules, chiffres et « - », 40 au plus). */
export function identifiantPropose(nom: string): string {
  const s = nom.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '')
  return s
}

export function nomDe(t: Tablette): string {
  return t.nom?.trim() || t.id
}

/* ─────────────────────────── filtres du parc ─────────────────────────── */

export const FILTRE_TOUTES = 'Toutes'
export const FILTRE_SURVEILLER = 'À surveiller'

export function filtresParc(ts: Tablette[], maintenant = Date.now()): { label: string; n: number }[] {
  const salles = SALLES.filter((s) => ts.some((t) => t.salle === s))
  const autres = [...new Set(ts.map((t) => t.salle).filter((s): s is string => !!s && !(SALLES as readonly string[]).includes(s)))]
  return [
    { label: FILTRE_TOUTES, n: ts.length },
    ...[...salles, ...autres].map((s) => ({ label: s, n: ts.filter((t) => t.salle === s).length })),
    { label: FILTRE_SURVEILLER, n: ts.filter((t) => aSurveiller(t, maintenant)).length },
  ]
}

export function filtrer(ts: Tablette[], filtre: string, maintenant = Date.now()): Tablette[] {
  if (filtre === FILTRE_TOUTES) return ts
  if (filtre === FILTRE_SURVEILLER) return ts.filter((t) => aSurveiller(t, maintenant))
  return ts.filter((t) => t.salle === filtre)
}

/** Tri : à surveiller d'abord, puis par salle, puis par nom. */
export function trier(ts: Tablette[], maintenant = Date.now()): Tablette[] {
  const rang: Record<Ton, number> = { off: 0, err: 1, warn: 2, maint: 3, ok: 4 }
  const salle = (t: Tablette) => { const i = (SALLES as readonly string[]).indexOf(t.salle ?? ''); return i < 0 ? 99 : i }
  return [...ts].sort((a, b) => rang[etatTablette(a, maintenant).ton] - rang[etatTablette(b, maintenant).ton]
    || salle(a) - salle(b) || nomDe(a).localeCompare(nomDe(b), 'fr'))
}

export function resume(ts: Tablette[], maintenant = Date.now()): { enLigne: number; total: number; ecransAllumes: number; veille: number; bloquees: number; surveiller: number; versions: string[] } {
  const enLigne = ts.filter((t) => t.en_ligne === true)
  return {
    enLigne: enLigne.length, total: ts.length,
    ecransAllumes: enLigne.filter((t) => t.etat?.ecran === 'ON' && !t.etat?.bloque).length,
    veille: enLigne.filter((t) => t.etat?.ecran === 'OFF').length,
    bloquees: ts.filter((t) => t.etat?.bloque).length,
    surveiller: ts.filter((t) => aSurveiller(t, maintenant)).length,
    versions: [...new Set(ts.map((t) => t.etat?.app).filter((v): v is string => !!v))].sort(),
  }
}

/* ─────────────────────────── dashboards de Home Assistant ─────────────────────────── */

export interface VueHa { id: string; titre: string; visible_par: string[] | 'tous' }
export interface DashboardHa { id: string; titre: string; vues: VueHa[] }

/** Normalise la liste de kiosk-api ({id|dashboard|url_path, titre, vues:[{id|path, titre}]}). */
export function dashboardsDe(v: unknown): DashboardHa[] {
  const liste = estObjet(v) && Array.isArray(v['dashboards']) ? v['dashboards'] : Array.isArray(v) ? v : []
  const id = (o: Record<string, unknown>, ...cles: string[]): string | null => {
    for (const k of cles) if (typeof o[k] === 'string' && o[k]) return o[k] as string
    return null
  }
  const out: DashboardHa[] = []
  for (const d of liste) {
    if (!estObjet(d)) continue
    const did = id(d, 'id', 'dashboard', 'url_path')
    if (!did) continue
    const vues: VueHa[] = []
    for (const x of Array.isArray(d['vues']) ? d['vues'] : []) {
      if (!estObjet(x)) continue
      const vid = id(x, 'id', 'vue', 'path')
      const vp = x['visible_par']
      const visible_par = Array.isArray(vp) ? vp.filter((c): c is string => typeof c === 'string') : 'tous'
      if (vid) vues.push({ id: vid, titre: id(x, 'titre', 'title') ?? vid, visible_par })
    }
    out.push({ id: did, titre: id(d, 'titre', 'title') ?? did, vues })
  }
  return out
}

/**
 * Les vues qu'une tablette peut afficher : celles visibles par son compte
 * Home Assistant (contrat : `visible_par`). Compte inconnu : toutes.
 */
export function vuesPour(d: DashboardHa, compte: string | null | undefined): VueHa[] {
  if (!compte) return d.vues
  return d.vues.filter((v) => v.visible_par === 'tous' || v.visible_par.includes(compte))
}

/** Les dashboards dont au moins une vue est visible par ce compte. */
export function dashboardsPour(ds: DashboardHa[], compte: string | null | undefined): DashboardHa[] {
  return ds.map((d) => ({ ...d, vues: vuesPour(d, compte) })).filter((d) => d.vues.length > 0)
}

/** Une ligne de bilan pour un geste de groupe (`bilan` rendu par Gestion). */
export function phraseBilan(b: { ok: number; attente: number; echec: number; total: number } | null | undefined, reussite: string): string {
  if (!b) return reussite
  if (b.echec === 0 && b.attente === 0) return `${reussite} (${b.ok}/${b.total}).`
  return `${b.ok}/${b.total} réussi${b.ok > 1 ? 's' : ''}${b.attente ? `, ${b.attente} en attente (hors ligne)` : ''}${b.echec ? `, ${b.echec} en échec` : ''}.`
}

/* ─────────────────────────── Wi-Fi (contrat 1.1.0) ─────────────────────────── */

export type SecuriteReseau = 'wpa2' | 'wpa3' | 'wpa2_wpa3' | 'entreprise' | 'wep' | 'ouvert' | 'inconnue'
export interface ReseauVisible { ssid: string; signal_dbm: number; bandes: string[]; securite: SecuriteReseau; connu: boolean; actuel: boolean }
export interface ReseauConnu { ssid: string; mac: 'reelle' | 'aleatoire' | null; appris_par_app: boolean }
export interface ScanWifi { actuel: string | null; frais: boolean; visibles: ReseauVisible[]; connus: ReseauConnu[] }
export interface ProfilWifi { nom: string; ssid: string | null; securite: 'wpa2' | 'wpa3' | 'wpa2_wpa3' | null }

const SECURITES: readonly string[] = ['wpa2', 'wpa3', 'wpa2_wpa3', 'entreprise', 'wep', 'ouvert', 'inconnue']
const BANDES: readonly string[] = ['2,4 GHz', '5 GHz', '6 GHz']
const texteCourt = (v: unknown): v is string => typeof v === 'string' && v.length >= 1 && v.length <= 64

/** Le résultat d'un balayage, relu champ par champ ; une ligne mal formée est écartée, pas inventée. */
export function scanWifiDe(v: unknown): ScanWifi | null {
  if (!estObjet(v) || !Array.isArray(v['visibles']) || !Array.isArray(v['connus'])) return null
  const visibles = (v['visibles'] as unknown[]).filter(estObjet).flatMap((x): ReseauVisible[] => (
    texteCourt(x['ssid']) && typeof x['signal_dbm'] === 'number' && Number.isFinite(x['signal_dbm'])
      ? [{
        ssid: x['ssid'], signal_dbm: Math.round(x['signal_dbm']),
        bandes: Array.isArray(x['bandes']) ? (x['bandes'] as unknown[]).filter((b): b is string => typeof b === 'string' && BANDES.includes(b)) : [],
        securite: (typeof x['securite'] === 'string' && SECURITES.includes(x['securite']) ? x['securite'] : 'inconnue') as SecuriteReseau,
        connu: x['connu'] === true, actuel: x['actuel'] === true,
      }]
      : []
  )).slice(0, 100)
  const connus = (v['connus'] as unknown[]).filter(estObjet).flatMap((x): ReseauConnu[] => (
    texteCourt(x['ssid'])
      ? [{ ssid: x['ssid'], mac: x['mac'] === 'reelle' || x['mac'] === 'aleatoire' ? x['mac'] : null, appris_par_app: x['appris_par_app'] === true }]
      : []
  )).slice(0, 100)
  return { actuel: texteCourt(v['actuel']) ? v['actuel'] : null, frais: v['frais'] !== false, visibles, connus }
}

export function profilsWifiDe(v: unknown): ProfilWifi[] {
  if (!estObjet(v) || !Array.isArray(v['profils'])) return []
  return (v['profils'] as unknown[]).filter(estObjet).flatMap((x): ProfilWifi[] => (texteCourt(x['nom'])
    ? [{
      nom: x['nom'], ssid: texteCourt(x['ssid']) ? x['ssid'] : null,
      securite: x['securite'] === 'wpa2' || x['securite'] === 'wpa3' || x['securite'] === 'wpa2_wpa3' ? x['securite'] : null,
    }]
    : []))
}

/** 0 à 4 barres : -55 dBm et mieux = 4, -67 = 3, -75 = 2, -85 = 1. */
export function barresSignal(dbm: number): 0 | 1 | 2 | 3 | 4 {
  return dbm >= -55 ? 4 : dbm >= -67 ? 3 : dbm >= -75 ? 2 : dbm >= -85 ? 1 : 0
}

export function libelleSecurite(s: SecuriteReseau): string {
  return ({ wpa2: 'WPA2', wpa3: 'WPA3', wpa2_wpa3: 'WPA2/WPA3', entreprise: 'Entreprise', wep: 'WEP', ouvert: 'Ouvert', inconnue: 'Sécurité inconnue' } as const)[s]
}

/** Le profil de kiosk-api qui porte ce SSID, s'il y en a un (la tablette n'apprend que WPA2 / WPA3). */
export function profilPour(ssid: string, profils: ProfilWifi[]): ProfilWifi | null {
  return profils.find((p) => p.ssid === ssid) ?? null
}

/**
 * Les lignes à afficher : les réseaux visibles (avec, s'il est connu, son
 * réglage de MAC), puis les réseaux connus hors de portée.
 */
export function lignesWifi(scan: ScanWifi): { ssid: string; visible: ReseauVisible | null; connu: ReseauConnu | null; actuel: boolean }[] {
  const parSsid = new Map(scan.connus.map((c) => [c.ssid, c]))
  const vus = new Set(scan.visibles.map((v) => v.ssid))
  return [
    ...scan.visibles.map((v) => ({ ssid: v.ssid, visible: v, connu: parSsid.get(v.ssid) ?? null, actuel: v.actuel || v.ssid === scan.actuel })),
    ...scan.connus.filter((c) => !vus.has(c.ssid)).map((c) => ({ ssid: c.ssid, visible: null, connu: c, actuel: c.ssid === scan.actuel })),
  ]
}

/* ─────────────────────────── erreurs ─────────────────────────── */


export function messageErreur(status: number, data: unknown, repli: string): string {
  const m = estObjet(data) ? data['message'] : null
  if (typeof m === 'string' && m.trim()) return m
  if (status === 403) return 'Vous n’avez pas accès à cette fonction.'
  if (status === 0) return 'Gestion n’a pas répondu.'
  return repli
}
